import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  SpinnerGap, CheckSquare, Square, Robot, Ranking, TextAa, Target, CurrencyDollar,
  TrendUp, TrendDown, CaretLeft, CaretRight,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const MODEL_COLORS = [
  "#FF4400", "#3B82F6", "#10B981", "#F59E0B", "#8B5CF6",
  "#EC4899", "#06B6D4", "#F97316", "#6366F1", "#14B8A6",
];

export function Pager({ page, pages, onPage }) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 pt-3">
      <button onClick={() => onPage(Math.max(1, page - 1))} disabled={page <= 1} className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-30"><CaretLeft size={16} /></button>
      <span className="text-xs text-[var(--text-muted)]">{page} / {pages}</span>
      <button onClick={() => onPage(Math.min(pages, page + 1))} disabled={page >= pages} className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-30"><CaretRight size={16} /></button>
    </div>
  );
}

export function ModelRow({ model, enabled, onToggle }) {
  return (
    <button onClick={onToggle} className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-[var(--bg-hover)] transition-colors border-b border-[var(--border)] last:border-b-0" data-testid={`model-row-${model.model_id}`}>
      {enabled ? <CheckSquare size={16} weight="fill" className="text-[#FF4400] shrink-0" /> : <Square size={16} className="text-[var(--text-muted)] shrink-0" />}
      <div className="flex-1 min-w-0 text-left">
        <span className="text-sm text-[var(--text-primary)]">{model.name}</span>
      </div>
      <span className="text-[10px] font-['JetBrains_Mono'] text-[var(--text-muted)] shrink-0">{model.model_id}</span>
    </button>
  );
}

export function AdminBarChart({ data, maxVal, colorMap }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="space-y-2">
      {data.map((item, i) => {
        const pct = maxVal > 0 ? (item.count / maxVal) * 100 : 0;
        const color = colorMap?.[item.label] || MODEL_COLORS[i % MODEL_COLORS.length];
        return (
          <div key={item.label} className="flex items-center gap-3" data-testid={`bar-${item.label}`}>
            <span className="text-[11px] text-[var(--text-secondary)] w-[140px] truncate text-right shrink-0" title={item.label}>{item.label}</span>
            <div className="flex-1 h-5 rounded bg-[var(--bg-base)] overflow-hidden relative">
              <motion.div initial={{ width: 0 }} animate={{ width: `${Math.max(pct, 1.5)}%` }} transition={{ duration: 0.5, delay: i * 0.04 }} className="h-full rounded" style={{ backgroundColor: color }} />
            </div>
            <span className="text-xs font-['JetBrains_Mono'] text-[var(--text-primary)] w-10 text-right shrink-0">{item.count}</span>
          </div>
        );
      })}
    </div>
  );
}

export function SummaryCard({ label, value, icon, small, accent }) {
  return (
    <div className={`border rounded-lg p-3 text-center ${accent ? "bg-[#FF4400]/5 border-[#FF4400]/20" : "bg-[var(--bg-card)] border-[var(--border)]"}`}>
      <div className={`flex items-center justify-center gap-1.5 mb-1 ${accent ? "text-[#FF4400]" : "text-[var(--text-muted)]"}`}>{icon}</div>
      <p className={`font-bold ${accent ? "text-[#FF4400]" : "text-[var(--text-primary)]"} ${small ? "text-sm truncate" : "text-xl font-['JetBrains_Mono']"}`}>{value}</p>
      <p className="text-[9px] text-[var(--text-muted)] uppercase tracking-wider mt-0.5">{label}</p>
    </div>
  );
}

export function MetricCard({ label, value, sub, trend }) {
  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-3" data-testid={`metric-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-[10px] font-['JetBrains_Mono'] text-[var(--text-muted)] uppercase tracking-wider mb-1">{label}</p>
      <div className="flex items-end gap-2">
        <span className="text-xl font-bold font-['JetBrains_Mono'] text-[var(--text-primary)]">{value}</span>
        {trend === "up" && <TrendUp size={14} className="text-emerald-400 mb-0.5" weight="bold" />}
        {trend === "down" && <TrendDown size={14} className="text-red-400 mb-0.5" weight="bold" />}
      </div>
      {sub && <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{sub}</p>}
    </div>
  );
}

export function MiniBarChart({ data, color = "#FF4400" }) {
  if (!data || data.length === 0) return <p className="text-xs text-[var(--text-muted)] text-center py-4">No data for this period</p>;
  const max = Math.max(...data.map((d) => d.count), 1);
  const displayData = data.slice(-14);
  return (
    <div className="flex items-end gap-[3px] h-20" data-testid="mini-bar-chart">
      {displayData.map((d, i) => (
        <div key={d.date || i} className="flex-1 flex flex-col items-center gap-0.5" title={`${d.date}: ${d.count}`}>
          <div className="w-full rounded-sm transition-all hover:opacity-80" style={{ height: `${Math.max((d.count / max) * 100, 4)}%`, backgroundColor: color, opacity: 0.8 }} />
          {displayData.length <= 7 && <span className="text-[8px] text-[var(--text-muted)]">{d.date?.slice(5)}</span>}
        </div>
      ))}
    </div>
  );
}

export function ModelAnalyticsPanel({ analytics, loading, period, onPeriodChange, budgetEditing, budgetDraft, setBudgetDraft, setBudgetEditing, saveBudget, budgetSaving }) {
  // Extract data with safe defaults (hooks must be called unconditionally)
  const model_usage = useMemo(() => analytics?.model_usage || [], [analytics?.model_usage]);
  const word_stats = useMemo(() => analytics?.word_stats || [], [analytics?.word_stats]);
  const cost_estimation = analytics?.cost_estimation || { total_estimated_cost: 0, by_model: [], note: "" };

  // useMemo hooks must be called before any early returns
  const colorMap = useMemo(() => {
    const map = {};
    model_usage.forEach((m, i) => { map[m.model] = MODEL_COLORS[i % MODEL_COLORS.length]; });
    return map;
  }, [model_usage]);

  const totalWords = useMemo(() => word_stats.reduce((s, w) => s + w.total_words, 0), [word_stats]);
  const costModelsWithSpend = useMemo(() => cost_estimation.by_model.filter(c => c.estimated_cost > 0), [cost_estimation.by_model]);

  // Early returns after hooks
  if (loading) return <div className="flex justify-center py-12"><SpinnerGap size={24} className="animate-spin text-[#FF4400]" /></div>;
  if (!analytics) return <p className="text-sm text-[var(--text-muted)] text-center py-8">No analytics data yet.</p>;

  const { total_generations, content_type_by_model, top_users, platform_usage, budget } = analytics;
  const maxModelCount = model_usage.length > 0 ? model_usage[0].count : 1;
  const maxPlatformCount = platform_usage.length > 0 ? platform_usage[0].count : 1;
  const costData = cost_estimation;
  const maxCost = costData.by_model.length > 0 ? costData.by_model[0].estimated_cost : 0.01;
  const b = budget || { monthly_budget: 0, alert_threshold_pct: 80, current_month_cost: 0, pct_used: 0, status: "ok" };

  const PERIODS = [{ value: "7d", label: "7 Days" }, { value: "30d", label: "30 Days" }, { value: "90d", label: "90 Days" }, { value: "all", label: "All Time" }];

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="model-analytics-panel">
      <div className="flex items-center justify-between">
        <div className="flex gap-1 rounded-lg border border-[var(--border)] overflow-hidden" data-testid="analytics-period-selector">
          {PERIODS.map(p => (
            <button key={p.value} onClick={() => onPeriodChange(p.value)} className={`px-3 py-1 text-[11px] font-medium transition-colors ${period === p.value ? "bg-[var(--bg-raised)] text-[var(--text-primary)]" : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"}`} data-testid={`period-${p.value}`}>{p.label}</button>
          ))}
        </div>
        <span className="text-xs font-['JetBrains_Mono'] text-[var(--text-muted)]" data-testid="total-generations-count">{total_generations.toLocaleString()} generations</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <SummaryCard label="Models Used" value={model_usage.length} icon={<Robot size={16} />} />
        <SummaryCard label="Top Model" value={model_usage[0]?.model?.replace("OpenRouter: ", "") || "—"} small icon={<Ranking size={16} />} />
        <SummaryCard label="Total Words" value={totalWords.toLocaleString()} icon={<TextAa size={16} />} />
        <SummaryCard label="Platforms" value={platform_usage.length} icon={<Target size={16} />} />
        <SummaryCard label="Est. Cost" value={`$${costData.total_estimated_cost.toFixed(2)}`} icon={<CurrencyDollar size={16} />} accent />
      </div>

      {b.monthly_budget > 0 && b.status !== "ok" && (
        <div className={`px-4 py-3 rounded-lg border flex items-center justify-between ${b.status === "exceeded" ? "bg-red-500/10 border-red-500/30 text-red-400" : "bg-amber-500/10 border-amber-500/30 text-amber-400"}`} data-testid="budget-alert-banner">
          <div className="flex items-center gap-2 text-xs font-medium">
            <CurrencyDollar size={16} weight="bold" />
            {b.status === "exceeded" ? `Budget exceeded! $${b.current_month_cost.toFixed(2)} of $${b.monthly_budget.toFixed(2)} (${b.pct_used}%)` : `Approaching budget: $${b.current_month_cost.toFixed(2)} of $${b.monthly_budget.toFixed(2)} (${b.pct_used}%)`}
          </div>
          <button onClick={() => setBudgetEditing(true)} className="text-[10px] underline opacity-80 hover:opacity-100">Edit Budget</button>
        </div>
      )}

      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4" data-testid="budget-settings-panel">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">Monthly Budget</h4>
          {!budgetEditing && <button onClick={() => setBudgetEditing(true)} className="text-[10px] text-[var(--text-muted)] hover:text-[#FF4400] transition-colors" data-testid="edit-budget-btn">Edit</button>}
        </div>
        {budgetEditing ? (
          <div className="space-y-3" data-testid="budget-edit-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider block mb-1">Budget Cap ($)</label>
                <input type="number" min="0" step="0.50" value={budgetDraft.monthly_budget} onChange={(e) => setBudgetDraft(d => ({ ...d, monthly_budget: parseFloat(e.target.value) || 0 }))} className="w-full bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-md px-3 py-1.5 text-sm text-[var(--text-primary)] font-['JetBrains_Mono'] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" data-testid="budget-input" />
              </div>
              <div>
                <label className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider block mb-1">Alert at (%)</label>
                <input type="number" min="1" max="99" value={budgetDraft.alert_threshold_pct} onChange={(e) => setBudgetDraft(d => ({ ...d, alert_threshold_pct: parseInt(e.target.value) || 80 }))} className="w-full bg-[var(--bg-base)] border border-[var(--border-strong)] rounded-md px-3 py-1.5 text-sm text-[var(--text-primary)] font-['JetBrains_Mono'] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" data-testid="alert-threshold-input" />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={saveBudget} disabled={budgetSaving} className="px-3 py-1.5 rounded-md text-xs bg-[#FF4400] text-white hover:bg-[#FF6633] transition-colors disabled:opacity-50" data-testid="save-budget-btn">{budgetSaving ? "Saving..." : "Save Budget"}</button>
              <button onClick={() => setBudgetEditing(false)} className="px-3 py-1.5 rounded-md text-xs border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {b.monthly_budget > 0 ? (
              <>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[var(--text-secondary)]">${b.current_month_cost.toFixed(2)} / ${b.monthly_budget.toFixed(2)}</span>
                  <span className={`font-['JetBrains_Mono'] font-medium ${b.status === "exceeded" ? "text-red-400" : b.status === "warning" ? "text-amber-400" : "text-green-400"}`} data-testid="budget-pct-display">{b.pct_used}%</span>
                </div>
                <div className="h-2 rounded-full bg-[var(--bg-base)] overflow-hidden" data-testid="budget-progress-bar">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(b.pct_used, 100)}%` }} transition={{ duration: 0.6 }} className={`h-full rounded-full ${b.status === "exceeded" ? "bg-red-500" : b.status === "warning" ? "bg-amber-500" : "bg-green-500"}`} />
                </div>
                <p className="text-[9px] text-[var(--text-muted)]">Alert at {b.alert_threshold_pct}% &middot; {b.status === "ok" ? "Within budget" : b.status === "warning" ? "Approaching limit" : "Over budget"}</p>
              </>
            ) : (
              <p className="text-xs text-[var(--text-muted)] py-1" data-testid="no-budget-set">No budget set. <button onClick={() => setBudgetEditing(true)} className="text-[#FF4400] hover:underline">Set a monthly cap</button> to track spend.</p>
            )}
          </div>
        )}
      </div>

      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4" data-testid="model-usage-chart">
        <h4 className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-3">Generations by Model</h4>
        <AdminBarChart data={model_usage.map(m => ({ label: m.model, count: m.count }))} maxVal={maxModelCount} colorMap={Object.fromEntries(model_usage.map((m, i) => [m.model, MODEL_COLORS[i % MODEL_COLORS.length]]))} />
      </div>

      {costModelsWithSpend.length > 0 && (
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4" data-testid="cost-breakdown-panel">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider">Estimated Cost by Model</h4>
            <span className="text-[10px] text-[var(--text-muted)] font-['JetBrains_Mono']" data-testid="total-cost-display">Total: ${costData.total_estimated_cost.toFixed(2)}</span>
          </div>
          <div className="space-y-2">
            {costModelsWithSpend.map((c, i) => {
              const pct = maxCost > 0 ? (c.estimated_cost / maxCost) * 100 : 0;
              const clr = colorMap[c.model] || MODEL_COLORS[i % MODEL_COLORS.length];
              return (
                <div key={c.model} data-testid={`cost-row-${c.model}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] text-[var(--text-secondary)] truncate flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: clr }} />{c.model}
                      {!c.has_pricing && <span className="text-[9px] px-1 py-0 rounded bg-[var(--bg-base)] border border-[var(--border)] text-[var(--text-muted)]">est.</span>}
                    </span>
                    <span className="text-xs font-['JetBrains_Mono'] text-[var(--text-primary)] shrink-0 ml-2">${c.estimated_cost.toFixed(4)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-3 rounded bg-[var(--bg-base)] overflow-hidden">
                      <motion.div initial={{ width: 0 }} animate={{ width: `${Math.max(pct, 2)}%` }} transition={{ duration: 0.5, delay: i * 0.04 }} className="h-full rounded" style={{ backgroundColor: clr, opacity: 0.7 }} />
                    </div>
                    <span className="text-[9px] font-['JetBrains_Mono'] text-[var(--text-muted)] w-28 text-right shrink-0">{c.input_tokens.toLocaleString()}in / {c.output_tokens.toLocaleString()}out</span>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[9px] text-[var(--text-muted)] mt-3 pt-2 border-t border-[var(--border)] leading-relaxed">{costData.note} Models without catalog pricing use fallback rates ($3/M input, $10/M output). <span className="inline-block ml-1 px-1 py-0 rounded bg-[var(--bg-base)] border border-[var(--border)]">est.</span> = fallback pricing.</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4" data-testid="content-type-breakdown">
          <h4 className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-3">Content Types by Top Models</h4>
          {Object.entries(content_type_by_model).length === 0 ? <p className="text-xs text-[var(--text-muted)] py-4 text-center">No data</p> : (
            <div className="space-y-4">
              {Object.entries(content_type_by_model).slice(0, 5).map(([model, types]) => (
                <div key={model}>
                  <p className="text-xs font-medium text-[var(--text-secondary)] mb-1.5 flex items-center gap-2"><span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: colorMap[model] || "#FF4400" }} />{model}</p>
                  <div className="flex flex-wrap gap-1.5">{types.slice(0, 5).map(t => <span key={t.type} className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--bg-base)] border border-[var(--border)] text-[var(--text-muted)]">{t.type} <span className="font-['JetBrains_Mono'] text-[var(--text-primary)]">{t.count}</span></span>)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4" data-testid="word-stats-panel">
          <h4 className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-3">Output per Model</h4>
          <div className="space-y-2">
            {word_stats.slice(0, 8).map((w, i) => (
              <div key={w.model} className="flex items-center justify-between text-xs py-1.5 border-b border-[var(--border)] last:border-b-0">
                <span className="text-[var(--text-secondary)] flex items-center gap-2 truncate"><span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: colorMap[w.model] || MODEL_COLORS[i % MODEL_COLORS.length] }} />{w.model}</span>
                <div className="flex items-center gap-4 shrink-0">
                  <span className="font-['JetBrains_Mono'] text-[var(--text-muted)]" title="Avg words per generation">~{w.avg_words} avg</span>
                  <span className="font-['JetBrains_Mono'] text-[var(--text-primary)] w-16 text-right" title="Total words">{w.total_words.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4" data-testid="platform-usage-chart">
          <h4 className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-3">Generations by Platform</h4>
          <AdminBarChart data={platform_usage.map(p => ({ label: p.platform, count: p.count }))} maxVal={maxPlatformCount} />
        </div>
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-4" data-testid="top-users-panel">
          <h4 className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-3">Top Users</h4>
          <div className="space-y-2">
            {top_users.map((u, i) => (
              <div key={u.user_id} className="flex items-center gap-3 py-1.5 border-b border-[var(--border)] last:border-b-0">
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0" style={{ backgroundColor: MODEL_COLORS[i] + "22", color: MODEL_COLORS[i] }}>{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[var(--text-primary)] truncate">{u.name || u.email}</p>
                  <p className="text-[10px] text-[var(--text-muted)] truncate">{u.email}</p>
                </div>
                <span className="text-sm font-['JetBrains_Mono'] font-semibold text-[var(--text-primary)]">{u.count}</span>
              </div>
            ))}
            {top_users.length === 0 && <p className="text-xs text-[var(--text-muted)] text-center py-3">No users in period.</p>}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function TemplateEditForm({ template, isNew, saving, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: template.name || "",
    category: template.category || "General",
    description: template.description || "",
    icp: template.icp || "",
    positioning: template.positioning || "",
    offer: template.offer || "",
    message_pillars: (template.message_pillars || []).join(", "),
    claims_to_avoid: (template.claims_to_avoid || []).join(", "),
    proof_points: (template.proof_points || []).join(", "),
  });

  const handleSubmit = () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    onSave({
      name: form.name.trim(),
      category: form.category.trim() || "General",
      description: form.description.trim(),
      icp: form.icp.trim(),
      positioning: form.positioning.trim(),
      offer: form.offer.trim(),
      message_pillars: form.message_pillars.split(",").map(s => s.trim()).filter(Boolean),
      claims_to_avoid: form.claims_to_avoid.split(",").map(s => s.trim()).filter(Boolean),
      proof_points: form.proof_points.split(",").map(s => s.trim()).filter(Boolean),
    });
  };

  const Field = ({ label, field, textarea }) => (
    <div>
      <label className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-[var(--text-muted)] mb-1 block">{label}</label>
      {textarea ? (
        <textarea value={form[field]} onChange={(e) => setForm(prev => ({ ...prev, [field]: e.target.value }))} rows={3} className="w-full px-3 py-2 bg-[var(--bg-base)] border border-[var(--border)] rounded-lg text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400] resize-none" data-testid={`template-field-${field}`} />
      ) : (
        <input value={form[field]} onChange={(e) => setForm(prev => ({ ...prev, [field]: e.target.value }))} className="w-full px-3 py-2 bg-[var(--bg-base)] border border-[var(--border)] rounded-lg text-xs text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]" data-testid={`template-field-${field}`} />
      )}
    </div>
  );

  return (
    <div className="p-4 bg-[var(--bg-card)] border border-[#FF4400]/30 rounded-lg space-y-3" data-testid={isNew ? "new-template-form" : "edit-template-form"}>
      <h4 className="text-sm font-semibold text-[var(--text-primary)]">{isNew ? "New Template" : "Edit Template"}</h4>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><Field label="Name *" field="name" /><Field label="Category" field="category" /></div>
      <Field label="Description" field="description" textarea />
      <Field label="ICP (Ideal Customer Profile)" field="icp" textarea />
      <Field label="Positioning" field="positioning" textarea />
      <Field label="Offer" field="offer" textarea />
      <Field label="Message Pillars (comma-separated)" field="message_pillars" />
      <Field label="Claims to Avoid (comma-separated)" field="claims_to_avoid" />
      <Field label="Proof Points (comma-separated)" field="proof_points" />
      <div className="flex gap-2 pt-1">
        <Button onClick={handleSubmit} disabled={saving} className="bg-[#FF4400] hover:bg-[#FF6633] text-white text-xs h-8" data-testid="template-save-btn">{saving ? <SpinnerGap size={14} className="animate-spin" /> : isNew ? "Create Template" : "Save Changes"}</Button>
        <Button variant="outline" onClick={onCancel} className="text-xs h-10 sm:h-8 border-[var(--border)]" data-testid="template-cancel-btn">Cancel</Button>
      </div>
    </div>
  );
}
